import * as React from 'react';
import SearchField from '../../components/SearchField';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { interactiveRow } from '../../components/a11y';

/* Venue directory — list with inline expansion showing defaults + history. */

export default function VenuesV2() {
    const { getVenues, getVenue, createVenue, deleteVenue } = useProduction();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<any | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [addFields, setAddFields] = useState({ name: '', address: '' });
    const [notice, setNotice] = useState<string | null>(null);

    const reload = async () => {
        setRows((await getVenues().catch(() => [])) ?? []);
    };

    const submitAdd = async () => {
        if (!addFields.name.trim()) return;
        const ok = await createVenue({
            name: addFields.name.trim(),
            address: addFields.address.trim() || null,
        })
            .then(() => true)
            .catch(() => false);
        setNotice(ok ? null : 'Add failed');
        if (ok) {
            setAddFields({ name: '', address: '' });
            setAddOpen(false);
            await reload();
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const data = await getVenues().catch(() => []);
            if (!mounted) return;
            setRows(data ?? []);
            setLoading(false);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = async (id: number) => {
        if (openId === id) {
            setOpenId(null);
            setDetail(null);
            return;
        }
        setOpenId(id);
        setDetail(null);
        setDetail(await getVenue(id).catch(() => null));
    };

    const q = search.trim().toLowerCase();
    const filtered = rows.filter(
        (r) =>
            !q ||
            (r.name ?? '').toLowerCase().includes(q) ||
            (r.address ?? '').toLowerCase().includes(q),
    );

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Jobs · Venues</div>

            <div className="mb-4 flex items-center gap-3">
                <span className="text-[13px] text-muted-foreground">
                    <b className="font-semibold text-foreground">
                        {filtered.length}
                    </b>{' '}
                    venues
                </span>
                {notice && (
                    <span className="text-[12px] font-semibold text-attention">
                        {notice}
                    </span>
                )}
                <div className="flex-1" />
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Search venues…"
                    className="w-full max-w-[260px]"
                />
                <button
                    type="button"
                    onClick={() => setAddOpen((o) => !o)}
                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-2 font-[inherit] text-[12.5px] font-semibold text-brand-foreground"
                >
                    Add venue
                </button>
            </div>

            {addOpen && (
                <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-2xl border border-dashed border-border p-3">
                    <input
                        autoFocus
                        className="ps-input w-[220px] text-[12.5px]"
                        placeholder="Venue name"
                        value={addFields.name}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                name: e.target.value,
                            }))
                        }
                    />
                    <input
                        className="ps-input min-w-[260px] flex-1 text-[12.5px]"
                        placeholder="Address (Google Places search lands with integrations)"
                        value={addFields.address}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                address: e.target.value,
                            }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitAdd();
                        }}
                    />
                    <button
                        type="button"
                        disabled={!addFields.name.trim()}
                        onClick={submitAdd}
                        className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            )}

            {loading && (
                <div>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                        />
                    ))}
                </div>
            )}
            {filtered.map((r) => (
                <React.Fragment key={r.id}>
                    <div
                        {...interactiveRow(() => toggle(+r.id))}
                        className="flex cursor-pointer items-center gap-3.5 border-t border-line px-0.5 py-[10px] text-[12.5px] first:border-t-0 hover:bg-sunk"
                    >
                        <span className="min-w-0 flex-1 basis-[140px] truncate font-semibold sm:flex-none sm:basis-auto sm:w-[220px]">
                            {r.name}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {r.address ?? '—'}
                        </span>
                        <span className="w-[100px] flex-none tabular-nums text-muted-foreground">
                            {r.activationCount ?? 0} events
                        </span>
                        <span className="w-[90px] flex-none text-faint">
                            {r.lastActivationAt
                                ? moment(r.lastActivationAt).format('MMM YYYY')
                                : '—'}
                        </span>
                        <span className="flex-none text-faint">
                            {openId === +r.id ? '▾' : '›'}
                        </span>
                    </div>
                    {openId === +r.id && (
                        <div className="border-t border-line bg-sunk px-4 py-4 text-[12.5px]">
                            {!detail && (
                                <span className="text-faint">Loading…</span>
                            )}
                            {detail && (
                                <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3">
                                    <div>
                                        <div className="ps-label mb-1">
                                            Load-in default
                                        </div>
                                        {detail.defaultLoadInInstructions ??
                                            '—'}
                                    </div>
                                    <div>
                                        <div className="ps-label mb-1">
                                            Parking default
                                        </div>
                                        {detail.defaultParkingInstructions ??
                                            '—'}
                                    </div>
                                    <div>
                                        <div className="ps-label mb-1">
                                            Internet
                                        </div>
                                        {detail.defaultInternetProvided
                                            ? (detail.defaultInternetDetails ??
                                              'Provided')
                                            : 'Not provided'}
                                    </div>
                                    <div className="col-span-2 md:col-span-3">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                if (
                                                    !window.confirm(
                                                        `Delete "${r.name}"? This can't be undone.`,
                                                    )
                                                )
                                                    return;
                                                const ok = await deleteVenue(
                                                    +r.id,
                                                )
                                                    .then(() => true)
                                                    .catch(() => false);
                                                setNotice(
                                                    ok
                                                        ? null
                                                        : 'Cannot delete — has linked productions',
                                                );
                                                if (ok) {
                                                    setOpenId(null);
                                                    await reload();
                                                }
                                            }}
                                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-attention"
                                        >
                                            Delete venue
                                        </button>
                                    </div>
                                    <div className="col-span-2 md:col-span-3">
                                        <div className="ps-label mb-1.5">
                                            Event history
                                        </div>
                                        {(detail.activations ?? []).length ===
                                            0 && (
                                            <span className="text-faint">
                                                None yet.
                                            </span>
                                        )}
                                        {(detail.activations ?? []).map(
                                            (a: any) => (
                                                <div
                                                    key={a.activationId}
                                                    className="flex items-center gap-3 border-t border-line py-1.5 first:border-t-0"
                                                >
                                                    <span className="w-[64px] flex-none tabular-nums text-faint">
                                                        {a.startAt
                                                            ? moment(
                                                                  a.startAt,
                                                              ).format('MMM D')
                                                            : '—'}
                                                    </span>
                                                    <span className="min-w-0 flex-1 truncate font-semibold">
                                                        {a.name}
                                                    </span>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </React.Fragment>
            ))}
        </main>
    );
}
